import type { ReactNode } from 'react';
import { enableIterativeDiffRendering } from '@gonfalon/dogfood-flags/dogfood-flags';
import classNames from 'clsx';
import type { Change } from 'diff';

import { buildCollapsibleDiffPlan, type ChangeGroup, expandUnchangedLinesLabel } from './diffCollapse';
import { diffElementClassNames } from './diffElementClassNames';
import { buildAddedLineWordDiffSpans, buildRemovedLineWordDiffSpans } from './diffWordSpans';
import type { ElementState, RenderText } from './types';
import { elementDataAttributes } from './utils';

import styles from './Diff.module.css';

const renderTextPlain: RenderText = (text) => text;

/**
 * Unified Diff Renderer
 *
 * This module provides utilities for rendering unified diffs with collapsible sections.
 * Grouping and collapse planning live in `./diffCollapse`.
 *
 * @fileoverview Utilities for rendering unified diffs with collapsible sections
 */

export type { ChangeGroup } from './diffCollapse';
export { groupChangesForCollapse } from './diffCollapse';

/**
 * Renders a diff with collapsible sections for unchanged content.
 */
export function renderCollapsibleDiff(
  groups: ChangeGroup[],
  expandedGroups: Set<number>,
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  renderText: RenderText | undefined,
  contextLines: number,
  onToggleExpand: (groupIndex: number) => void,
): React.ReactNode[] {
  const elements: React.ReactNode[] = [];

  for (const step of buildCollapsibleDiffPlan(groups, expandedGroups, contextLines)) {
    if (step.kind === 'render') {
      elements.push(...renderDiff(step.changes, showLineNumbers, includeSubDiffs, renderText, [], step.startLine));
    } else {
      const label = expandUnchangedLinesLabel(step.hiddenCount, step.position);
      elements.push(
        <button
          key={`expand-${step.groupIndex}`}
          className={styles.expandSection}
          onClick={() => onToggleExpand(step.groupIndex)}
          type="button"
          aria-label={label}
        >
          <div className={styles.expandButton} data-testid="expand-unchanged-lines">
            <span className={styles.expandIcon}>⋯</span>
            <span className={styles.expandText}>{label}</span>
          </div>
        </button>,
      );
    }
  }

  return elements;
}

/**
 * Renders individual diff changes into React elements.
 *
 * `enable-iterative-diff-rendering` selects between the iterative walk and the
 * legacy recursive walk, which overflows the JS stack on large diffs.
 */
export function renderDiff(
  diff: Change[],
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  renderText: RenderText = renderTextPlain,
  elements: React.ReactNode[] = [],
  lineNumber: number = 1,
  prevItem: Change | null = null,
): React.ReactNode[] {
  return enableIterativeDiffRendering()
    ? renderDiffIteratively(diff, showLineNumbers, includeSubDiffs, renderText, elements, lineNumber, prevItem)
    : renderDiffRecursively(diff, showLineNumbers, includeSubDiffs, renderText, elements, lineNumber, prevItem);
}

function renderDiffIteratively(
  diff: Change[],
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  renderText: RenderText,
  elements: React.ReactNode[],
  lineNumber: number,
  prevItem: Change | null,
): React.ReactNode[] {
  let currentLineNumber = lineNumber;
  let previous = prevItem;

  for (let i = 0; i < diff.length; i += 1) {
    const current = diff[i];
    const next = diff[i + 1];
    const elementState: ElementState = current.added ? 'added' : current.removed ? 'removed' : 'unchanged';
    let innerElements: ReactNode[] = [renderText(current.value, `line-${currentLineNumber}`)];

    if (current.added && previous?.removed && includeSubDiffs) {
      innerElements = buildAddedLineWordDiffSpans(previous.value, current.value, currentLineNumber, renderText);
    }

    if (current.removed && next?.added && includeSubDiffs) {
      innerElements = buildRemovedLineWordDiffSpans(current.value, next.value, currentLineNumber, renderText);
    }

    if (current.value.trim() === '') {
      innerElements = [<br key={`empty-${currentLineNumber}`} />];
    }

    const elementClassName = diffElementClassNames[elementState];
    const dataAttribute = elementDataAttributes[elementState];

    elements.push(
      <div
        key={current.value + currentLineNumber}
        className={classNames(elementClassName, {
          [styles.showLineNumbers]: showLineNumbers,
        })}
        {...{ [`data-${dataAttribute}`]: true }}
      >
        {showLineNumbers && (
          <div data-line-number className={styles.lineNumber}>
            {currentLineNumber}
          </div>
        )}

        {showLineNumbers && elementState === 'unchanged' && (
          <div data-line-number className={`${styles.lineNumber} ${styles.lineNumberUnchanged}`}>
            {currentLineNumber}
          </div>
        )}

        {innerElements}
      </div>,
    );

    if (next) {
      currentLineNumber = current.removed && next.added ? currentLineNumber : currentLineNumber + 1;
      previous = current;
    }
  }

  return elements;
}

function renderDiffRecursively(
  diff: Change[],
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  renderText: RenderText,
  elements: React.ReactNode[],
  lineNumber: number,
  prevItem: Change | null,
): React.ReactNode[] {
  if (diff.length === 0) {
    return elements;
  }

  const [current, ...rest] = diff;
  const elementState: ElementState = current.added ? 'added' : current.removed ? 'removed' : 'unchanged';
  const next = rest[0];
  let innerElements: ReactNode[] = [renderText(current.value, `line-${lineNumber}`)];

  if (current.added && prevItem?.removed && includeSubDiffs) {
    innerElements = buildAddedLineWordDiffSpans(prevItem.value, current.value, lineNumber, renderText);
  }

  if (current.removed && next?.added && includeSubDiffs) {
    innerElements = buildRemovedLineWordDiffSpans(current.value, next.value, lineNumber, renderText);
  }

  if (current.value.trim() === '') {
    innerElements = [<br key={`empty-${lineNumber}`} />];
  }

  const elementClassName = diffElementClassNames[elementState];
  const dataAttribute = elementDataAttributes[elementState];

  const ammendedElements = [
    ...elements,
    <div
      key={current.value + lineNumber}
      className={classNames(elementClassName, {
        [styles.showLineNumbers]: showLineNumbers,
      })}
      {...{ [`data-${dataAttribute}`]: true }}
    >
      {showLineNumbers && (
        <div data-line-number className={styles.lineNumber}>
          {lineNumber}
        </div>
      )}

      {showLineNumbers && elementState === 'unchanged' && (
        <div data-line-number className={`${styles.lineNumber} ${styles.lineNumberUnchanged}`}>
          {lineNumber}
        </div>
      )}

      {innerElements}
    </div>,
  ];

  if (!next) {
    return ammendedElements;
  }

  const nextLineNumber = current.removed && next.added ? lineNumber : lineNumber + 1;

  return renderDiffRecursively(
    rest,
    showLineNumbers,
    includeSubDiffs,
    renderText,
    ammendedElements,
    nextLineNumber,
    current,
  );
}
